<template>
  <div>
    <div>
      <label
        v-if="title !== ''"
        class="ml-2 flex items-center justify-between text-neutral-800"
        :class="[
          size === 'small' ? 'text-sm' : '',
          size === 'medium' ? 'text-base' : '',
          size === 'large' ? 'text-lg' : '',
          font === 'normal' ? 'flex items-center justify-between text-neutral-800' : '',
          font === 'bold' ? 'flex items-center justify-between font-extrabold text-neutral-800' : ''
        ]"
      >
        {{ title }}
      </label>
      <label class="ml-2 mb-4 flex items-center justify-between text-xs font-extralight text-neutral-500">
        {{ subtitle || '&nbsp;' }}
      </label>
    </div>

    <div :id="id" class="e-conn-messages-chart highcharts-light" />
  </div>
</template>

<script lang="ts" setup>
import * as Highcharts from 'highcharts';
import { Chart, SeriesOptionsType } from 'highcharts';
import 'highcharts/modules/no-data-to-display';
import { onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';

interface Props {
  id: string;
  title: string;
  subtitle?: string;
  series: SeriesOptionsType[];
  yAxisLabel?: string;
  size?: 'small' | 'medium' | 'large';
  font?: 'normal' | 'bold';
  heightInPx?: number;
}

const props = withDefaults(defineProps<Props>(), {
  subtitle: '',
  yAxisLabel: '{value}',
  size: 'large',
  font: 'normal',
  heightInPx: 250
});

const { t } = useI18n();

onMounted(() => {
  drawGraph();
});

watch(props, () => drawGraph());

const drawGraph = (): Chart => {
  return Highcharts.chart({
    title: {
      text: ''
    },
    chart: {
      type: 'spline',
      height: props.heightInPx + 'px',
      renderTo: props.id,
      styledMode: true
    },
    legend: {
      enabled: false
    },
    xAxis: {
      type: 'datetime',
      tickPixelInterval: 100,
      labels: {
        format: '{value:%H:%M}'
      }
    },
    yAxis: {
      allowDecimals: false,
      title: {
        text: ''
      },
      labels: {
        format: props.yAxisLabel
      }
    },
    tooltip: {
      shared: true,
      xDateFormat: '%Y-%m-%d %H:%M:%S'
    },
    plotOptions: {
      spline: {
        marker: {
          symbol: 'circle',
          radius: 3
        },
        animation: false
      }
    },
    series: props.series,
    accessibility: {
      enabled: false
    },
    credits: {
      enabled: false
    },
    lang: {
      noData: t('common.noData')
    },
    noData: {
      useHTML: true
    }
  });
};
</script>

<style lang="scss">
.e-conn-messages-chart {
  .series-requests {
    fill: theme('colors.primary.300');
    stroke: theme('colors.primary.300');
  }

  .series-requests .highcharts-point {
    fill: theme('colors.primary.300');
    stroke: theme('colors.primary.300');
  }

  .series-responses {
    fill: theme('colors.blue.300');
    stroke: theme('colors.blue.300');
  }

  .series-responses .highcharts-point {
    fill: theme('colors.blue.300');
    stroke: theme('colors.blue.300');
  }

  .series-errors {
    fill: theme('colors.error.300');
    stroke: theme('colors.error.300');
  }

  .series-errors .highcharts-point {
    fill: theme('colors.error.300');
    stroke: theme('colors.error.300');
  }
}
</style>
